/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useState, useCallback, useMemo } from 'react';
import { Button, SelectOption, useBoundForm, Table, Th, LoadingSpinner } from 'common.ui';
import {
  GenericValidationError,
  UserType,
  RoleCreateRequest,
  RoleUpdateRequest,
  PermissionResponse,
  UserAuthenticationConstraint,
  ApplicationResponse,
  OrganizationGroupType,
  OrganizationGroupResponse,
  AdgangAccessControl,
  ResourceFilter,
  AccessRequestApprovalType
} from 'api/adgang/models';
import { ResponseError } from 'api/adgang';
import { Col, Container, Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { useApplications, usePermissions, useOrganizationGroups } from 'api/hooks';
import { useUserAuthenticationConstraints } from 'api/hooks/useUserAuthenticationConstraint';
import { PermissionsMultiSelect } from 'components/permissions/PermissionsMultiSelect';
import RoutePaths from 'RoutePaths';
import { ReactComponent as IconCross } from 'assets/icons/cross-icon.svg';

import { useAccessControlList } from 'hooks/access/useAccessControlList';
import OrganizationGroupSelector, {
  OrganizationGroup
} from 'components/organizationgroups/OrganizationGroupSelector';
import { RadioOption } from 'common.ui';

type RoleFormValue = RoleCreateRequest | RoleUpdateRequest;

type RoleFormProps<T extends RoleFormValue> = {
  initialValue?: T;
  resourceBasedAcl?: AdgangAccessControl[];
  isUnlocksExternalApproverRightsRole?: boolean;
  onSave: (model: T) => Promise<void>;
  isUpdate?: boolean;
  onSaveLabel: string;
};

type Role = {
  applicationId: string;
  name: string;
  shortName: string;
  userType: string;
  canBeRequestedByUsers: string;
  description: string;
  tags: string;
  permissionIds: string[];
  organizationGroupId: string;
  organizationGroupIds: string[];
  userAuthenticationConstraint?: UserAuthenticationConstraint;
  requireUserOrganizationInMinSide?: string;
  organizationGroupType?: OrganizationGroupType;
  accessRequestApprovalType: AccessRequestApprovalType;
  requiresMfa: string;
  allowEndUserCreation?: string;
  allowEndUserAssignment?: string;
  requiresReasonForAsking: string;
  helpTextReasonField: string;
};

type AddRoleScreenProps<T extends RoleFormValue> = {
  applications: ApplicationResponse[];
  applicationOptions: SelectOption[];
  permissionApplicationOptions: SelectOption[];
  permissions: PermissionResponse[];
  organizationGroups: OrganizationGroupResponse[];
  organizationGroupOptions: SelectOption[];
  initialValue?: T;
  isUpdate?: boolean;
  resourceBasedAcl?: AdgangAccessControl[];
  isUnlocksExternalApproverRightsRole?: boolean;
  onSave: (model: T) => Promise<void>;
  onSaveLabel: string;
};

function RoleForm<T extends RoleFormValue>({
  initialValue,
  resourceBasedAcl,
  isUnlocksExternalApproverRightsRole,
  onSave,
  isUpdate,
  onSaveLabel
}: RoleFormProps<T>): React.ReactElement<RoleFormProps<T>> {
  const [, hasAccess] = useAccessControlList();
  const [applicationOptions, applications] = useApplications(
    undefined,
    ResourceFilter.Role,
    !hasAccess(AdgangAccessControl.RolesViewShowNoAppFilter)
  );
  const [permissionApplicationOptions] = useApplications();
  const [, permissions] = usePermissions();
  const [organizationGroupOptions, organizationGroups] = useOrganizationGroups();

  return applications &&
    applicationOptions &&
    permissions &&
    organizationGroups &&
    organizationGroupOptions ? (
    <RoleFormScreen
      onSave={onSave}
      onSaveLabel={onSaveLabel}
      resourceBasedAcl={resourceBasedAcl}
      initialValue={initialValue}
      applications={applications}
      isUnlocksExternalApproverRightsRole={isUnlocksExternalApproverRightsRole}
      permissionApplicationOptions={permissionApplicationOptions ?? []}
      applicationOptions={applicationOptions}
      permissions={permissions}
      isUpdate={isUpdate}
      organizationGroups={organizationGroups}
      organizationGroupOptions={organizationGroupOptions}
    />
  ) : (
    <LoadingSpinner />
  );
}

function RoleFormScreen<T extends RoleFormValue>({
  applications,
  applicationOptions,
  permissionApplicationOptions,
  permissions,
  isUpdate,
  organizationGroups,
  organizationGroupOptions,
  isUnlocksExternalApproverRightsRole,
  initialValue,
  resourceBasedAcl,
  onSave,
  onSaveLabel
}: AddRoleScreenProps<T>) {
  const [apiErrors, setApiErrors] = useState<GenericValidationError>();
  const [userAuthenticationConstraints] = useUserAuthenticationConstraints(false, true);
  const [showAddPermissions, setShowAddPermissions] = useState(false);

  const history = useHistory();

  // @ts-ignore
  const [values, setValues] = useState<Role>({
    ...initialValue,
    applicationId: initialValue?.isGlobal ? 'global' : initialValue?.applicationId ?? '',
    permissionIds: initialValue?.permissionIds ? initialValue.permissionIds.map((p) => p.toString()) : [],
    organizationGroupIds: initialValue?.organizationGroupIds
      ? initialValue.organizationGroupIds.map((orgId) => orgId.toString())
      : [],
    organizationGroupId: initialValue?.organizationGroupIds
      ? initialValue.organizationGroupIds[0]?.toString()
      : '',
    canBeRequestedByUsers: initialValue?.canBeRequestedByUsers ? 'yes' : 'no',
    requireUserOrganizationInMinSide: initialValue?.requireUserOrganizationInMinSide ? 'yes' : 'no',
    requiresMfa: initialValue?.requiresMfa ? 'yes' : 'no',
    allowEndUserCreation: initialValue?.allowEndUserCreation ? 'on' : '',
    allowEndUserAssignment: initialValue?.allowEndUserAssignment ? 'on' : '',
    requiresReasonForAsking: initialValue?.requiresReasonForAsking ? 'yes' : 'no',
    helpTextReasonField: initialValue?.helpTextReasonField ?? ''
  });

  const resolveSelectedOrganizationGroups = (currentValues: Role): OrganizationGroup[] =>
    currentValues.organizationGroupIds
      .filter((orgId: string) => orgId !== '0')
      .map((orgId: string) => organizationGroupOptions?.find((el) => el.id === orgId))
      .filter((group): group is OrganizationGroup => group !== undefined);

  const [selectedOrganizationGroups, setSelectedOrganizationGroups] = useState<OrganizationGroup[]>(() =>
    isUnlocksExternalApproverRightsRole ? resolveSelectedOrganizationGroups(values) : []
  );

  // Synkroniserer valgte organisasjonsgrupper under render i stedet for i en effekt
  const [organizationGroupSource, setOrganizationGroupSource] = useState({
    organizationGroups,
    organizationGroupOptions,
    values,
    isUnlocksExternalApproverRightsRole
  });
  if (
    organizationGroupSource.organizationGroups !== organizationGroups ||
    organizationGroupSource.organizationGroupOptions !== organizationGroupOptions ||
    organizationGroupSource.values !== values ||
    organizationGroupSource.isUnlocksExternalApproverRightsRole !== isUnlocksExternalApproverRightsRole
  ) {
    setOrganizationGroupSource({
      organizationGroups,
      organizationGroupOptions,
      values,
      isUnlocksExternalApproverRightsRole
    });
    if (isUnlocksExternalApproverRightsRole) {
      setSelectedOrganizationGroups(resolveSelectedOrganizationGroups(values));
    }
  }

  const selectedApplication: ApplicationResponse | undefined = applications?.find(
    (a) => a.id === values?.applicationId
  );
  const requireUserAuthConstraint =
    values.applicationId === 'global' ||
    selectedApplication?.userAuthenticationConstraint === UserAuthenticationConstraint.RequiresChildConfiguration ||
    false;

  const [formAccessRequestApprovalTypeSeq, setFormAccessRequestApprovalTypeSeq] = useState(0);

  const [, hasAccessHook] = useAccessControlList();
  const hasAccess = useCallback(
    (access: AdgangAccessControl) => {
      if (!isUpdate) {
        return true;
      }

      return (!!resourceBasedAcl && resourceBasedAcl.find((x) => x === access)) || hasAccessHook(access);
    },
    [hasAccessHook, isUpdate, resourceBasedAcl]
  );

  const isAllowedCreationExternalUsers = hasAccess(AdgangAccessControl.AllowCreationExternalUsers);
  const isReadOnly = !hasAccess(AdgangAccessControl.RoleEdit);

  const accessRequestApprovalTypeOptions = useMemo(() => {
    const options: RadioOption[] = [
      {
        id: AccessRequestApprovalType.NoApproval,
        text: 'Ingen godkjenning nødvendig'
      },
      {
        id: AccessRequestApprovalType.LocalAdministrator,
        text: 'Lokal administrator (Applikasjonsansvarlig)'
      }
    ];

    if (values.userType === UserType.External) {
      options.push({ id: AccessRequestApprovalType.ExternalAdministrator, text: 'Ekstern godkjenner' });
    }

    return options;
  }, [values.userType]);

  const OnFormSubmit = useCallback(
    async (submitModel: Role) => {
      const model = {
        ...submitModel,
        permissionIds: values.permissionIds,
        organizationGroupIds: selectedOrganizationGroups.map((orgGroup: OrganizationGroup) => orgGroup.id)
      };

      let orgGroupIds = ['-1'];

      if (isUnlocksExternalApproverRightsRole) {
        orgGroupIds = model.organizationGroupIds;
      } else if (model.organizationGroupIds !== undefined && model.organizationGroupId !== '') {
        orgGroupIds = [model.organizationGroupId];
      }

      // Internal admin-rolle med AllowEndUserCreation skal kunne knyttes til OrgGroup
      // (Beslutning #14 i 2026-05-04-role-end-user-assignment-design.md). Andre Internal-roller
      // beholder gammel atferd (ingen OrgGroup).
      const isInternalAllowEndUserCreation =
        model.userType === UserType.Internal && model.allowEndUserCreation?.toString() === 'on';
      if (model.userType === UserType.Internal && !isInternalAllowEndUserCreation) {
        orgGroupIds = [];
      }

      setValues(model);
      try {
        // @ts-ignore
        await onSave({
          applicationId: model.applicationId !== 'global' ? model.applicationId : undefined,
          isGlobal: model.applicationId === 'global',
          description: model.description,
          name: model.name,
          shortName: model.shortName,
          userType: model.userType ? (model.userType as UserType) : undefined,
          tags: model.tags,
          requiresReasonForAsking: model.requiresReasonForAsking === 'yes',
          helpTextReasonField: model.helpTextReasonField,
          canBeRequestedByUsers: model.canBeRequestedByUsers === 'yes',
          permissionIds: model.permissionIds.map((p) => +p),
          organizationGroupIds: orgGroupIds,
          userAuthenticationConstraint: model.userAuthenticationConstraint
            ? (model.userAuthenticationConstraint as UserAuthenticationConstraint)
            : undefined,
          requireUserOrganizationInMinSide: orgGroupIds.length >= 1 && orgGroupIds[0] !== '-1',
          organizationGroupType: model.organizationGroupType
            ? (model.organizationGroupType as OrganizationGroupType)
            : undefined,
          accessRequestApprovalType: model.accessRequestApprovalType,
          requiresMfa: (model.requiresMfa as unknown) === 'yes',
          allowEndUserCreation: model.allowEndUserCreation?.toString() === 'on',
          allowEndUserAssignment: model.allowEndUserAssignment?.toString() === 'on'
        });

        setApiErrors(undefined);
        history.push(RoutePaths.roles);
      } catch (e) {
        // @ts-ignore
        if (e instanceof ResponseError) {
          // @ts-ignore
          const result = (await e.response.json()) as GenericValidationError;
          if (result) {
            setApiErrors(result);
          }
        }
      }
    },
    [history, onSave, values.permissionIds, selectedOrganizationGroups, isUnlocksExternalApproverRightsRole]
  );

  const { form, FormContainer, RadioGroup, Dropdown, Input, DisplayErrors, TextArea, Checkbox } = useBoundForm<Role>({
    onSubmit: async (e) => {
      await OnFormSubmit(e);
    },
    errors: apiErrors,
    model: values
  });

  const setUserType = (userType: UserType) => {
    // allowEndUserAssignment er kun gyldig for External-roller — nullstill ved bytte til Internal
    const allowEndUserAssignment = userType === UserType.Internal ? '' : values.allowEndUserAssignment;
    if (
      values.accessRequestApprovalType === AccessRequestApprovalType.ExternalAdministrator &&
      userType === UserType.Internal
    ) {
      setValues({
        ...values,
        userType,
        allowEndUserAssignment,
        accessRequestApprovalType: AccessRequestApprovalType.LocalAdministrator
      });
      setFormAccessRequestApprovalTypeSeq(formAccessRequestApprovalTypeSeq + 1);
    } else {
      setValues({ ...values, userType, allowEndUserAssignment });
    }
  };

  const onOrganisationGroupChange = useCallback((orgGroups: OrganizationGroup[]): void => {
    setSelectedOrganizationGroups(orgGroups);
  }, []);

  const renderOrganizationSelect = () => {
    if (!isUnlocksExternalApproverRightsRole) {
      return (
        <Dropdown
          form={form}
          readonly={isReadOnly || !hasAccess(AdgangAccessControl.RoleEditChangeOrganizationGroup)}
          name='organizationGroupId'
          label='Forbeholdt eksterne brukere fra organisasjonsgruppe'
          placeholder=''
          options={organizationGroupOptions}
          onChange={(e) => {
            const val = e.target.value;
            setValues({
              ...values,
              organizationGroupIds: Array.isArray(val) ? val : [val],
              requireUserOrganizationInMinSide: isUnlocksExternalApproverRightsRole ? 'yes' : 'no'
            });
          }}
        />
      );
    }
    return (
      <OrganizationGroupSelector
        onChanged={onOrganisationGroupChange}
        organizationGroups={selectedOrganizationGroups}
        readonly={isReadOnly || !hasAccess(AdgangAccessControl.RoleEditChangeOrganizationGroup)}
        selectOptions={organizationGroupOptions}
      />
    );
  };

  return (
    <Container fluid>
      <FormContainer form={form}>
        <Row>
          <Col sm={12} lg={6}>
            <DisplayErrors form={form} />
          </Col>
        </Row>
        <Row>
          <Col sm={6} lg={4}>
            <Input readonly={isUpdate} form={form} name='shortName' label='Kortnavn' placeholder='Kortnavn' />

            <Dropdown
              form={form}
              name='applicationId'
              label='Applikasjon'
              readonly={(isUpdate && !hasAccess(AdgangAccessControl.RoleEditChangeApplication)) || isReadOnly}
              options={applicationOptions}
              onChange={(e) => {
                const val = e.target.value as string;
                setValues({ ...values, applicationId: val });
              }}
            />

            <RadioGroup
              form={form}
              readonly={isReadOnly || !hasAccess(AdgangAccessControl.RoleEditChangeUserType)}
              name='userType'
              label='Hvilken brukergruppe er rollen ment for?'
              options={[
                { id: UserType.Internal, text: 'Interne ansatte' },
                { id: UserType.External, text: 'Eksterne brukere' }
              ]}
              onChange={(e) => {
                setUserType(e as UserType);
              }}
            />

            {(values.userType === UserType.External || values.allowEndUserCreation?.toString() === 'on') ? renderOrganizationSelect() : null}

            <RadioGroup
              form={form}
              key={formAccessRequestApprovalTypeSeq}
              readonly={isReadOnly || !hasAccess(AdgangAccessControl.RoleEditChangeAccessRequestApprovalType)}
              name='accessRequestApprovalType'
              label='Skal rolle godkjennes av lokal administrator?'
              options={accessRequestApprovalTypeOptions}
              onChange={(e) => {
                setValues({ ...values, accessRequestApprovalType: e as AccessRequestApprovalType });
              }}
              layout='vertical'
            />

            <TextArea
              form={form}
              readonly={isReadOnly || !hasAccess(AdgangAccessControl.RoleEditChangeDescription)}
              name='description'
              label='Beskrivelse til bruk i MinSide'
              placeholder='Beskrivelse'
            />

            {!showAddPermissions && (
              <Button
                disabled={isReadOnly || !hasAccess(AdgangAccessControl.RoleEditChangePermissions)}
                type='button'
                text=' + Legg til tilganger'
                onClick={() => setShowAddPermissions(true)}
                styleType='light'
              />
            )}
          </Col>
          <Col sm={6} lg={4}>
            <Input
              readonly={isReadOnly || !hasAccess(AdgangAccessControl.RoleEditChangeName)}
              form={form}
              name='name'
              label='Rollenavn'
              placeholder='Rollenavn'
            />

            <RadioGroup
              form={form}
              readonly={isReadOnly || !hasAccess(AdgangAccessControl.RoleEditChangeCanBeRequestedByUsers)}
              name='canBeRequestedByUsers'
              label='Er rollen søkbar i MinSide?'
              options={[
                { id: 'yes', text: 'Ja' },
                { id: 'no', text: 'Nei' }
              ]}
            />

            {isAllowedCreationExternalUsers && (
              <Checkbox
                form={form}
                name='allowEndUserCreation'
                label='Kan opprette sluttbrukere'
                question=''
                disabled={values.allowEndUserAssignment?.toString() === 'on'}
                onChange={(e) => {
                  const checked = (e.target as HTMLInputElement).checked;
                  setValues({ ...values, allowEndUserCreation: checked ? 'on' : '' });
                }}
              />
            )}
            {isAllowedCreationExternalUsers && values.userType === UserType.External && (
              <Checkbox
                form={form}
                name='allowEndUserAssignment'
                label='Kan tildeles ved opprettelse av sluttbruker'
                question=''
                disabled={values.allowEndUserCreation?.toString() === 'on'}
                onChange={(e) => {
                  const checked = (e.target as HTMLInputElement).checked;
                  setValues({ ...values, allowEndUserAssignment: checked ? 'on' : '' });
                }}
              />
            )}

            <RadioGroup
              form={form}
              name='requiresMfa'
              label='Krever rollen multi-faktor autentisering for eksterne brukere?'
              options={[
                { id: 'yes', text: 'Ja' },
                { id: 'no', text: 'Nei' }
              ]}
            />

            {requireUserAuthConstraint && (
              <Dropdown
                readonly={
                  isReadOnly || !hasAccess(AdgangAccessControl.RoleEditChangeUserAuthenticationConstraint)
                }
                form={form}
                name='userAuthenticationConstraint'
                label='Type brukerautentisering'
                options={userAuthenticationConstraints}
              />
            )}

            <Dropdown readonly form={form} name='varighet' label='Varighet for rettighet' options={[]} />

            <RadioGroup
              form={form}
              name='requiresReasonForAsking'
              label='Må brukeren oppgi en begrunnelse for å få denne rollen?'
              options={[
                { id: 'yes', text: 'Ja' },
                { id: 'no', text: 'Nei' }
              ]}
            />

            <TextArea
              form={form}
              readonly={isReadOnly || !hasAccess(AdgangAccessControl.RoleEditChangeHelpTextReason)}
              name='helpTextReasonField'
              label='Hjelpetekst for begrunnelse ved rettighetsforespørsel'
              placeholder='Legg til en beskrivelse som forklarer hva slags informasjon brukeren bør skrive i begrunnelsefeltet ved rettighetsforespørsel'
            />

            {/* <TextArea
              form={form}
              readonly={isReadOnly || !hasAccess(AdgangAccessControl.RoleEditChangeTags)}
              name='tags'
              label='Stikkord til bruk i MinSide'
              placeholder='Legg til stikkord'
            /> */}
          </Col>
        </Row>

        <Row>
          <Col></Col>
          <Col sm={12} lg={4}>
            <div className='float-right'>
              <Button disabled={isReadOnly} type='submit' text={onSaveLabel} />
              <Button
                disabled={isReadOnly}
                type='reset'
                text='Avbryt'
                styleType='light'
                onClick={() => history.push(RoutePaths.roles)}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            {!showAddPermissions && values.permissionIds?.length > 0 && permissions && (
              <Table>
                <thead>
                  <tr>
                    <Th title='Tilgangsnavn' />
                    <Th title='Beskrivelse' />
                    <Th title='Fjern' />
                  </tr>
                </thead>
                <tbody>
                  {values.permissionIds
                    .map((p) => permissions.filter((o) => o.permissionId?.toString() === p)[0])
                    .sort((p1, p2) => (p1.name || '').localeCompare(p2.name || ''))
                    .map((permission) => {
                      return (
                        <tr key={permission.permissionId}>
                          <td>{permission.name}</td>
                          <td>{permission.description}</td>
                          <td>
                            <IconCross
                              style={{ width: '12px' }}
                              onClick={() =>
                                setValues({
                                  ...values,
                                  permissionIds: values.permissionIds.filter(
                                    (v) => v !== permission.permissionId?.toString()
                                  )
                                })
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </Table>
            )}
            {showAddPermissions && (
              <>
                <b>Legg til tilgang</b>
                <PermissionsMultiSelect
                  applicationOptions={permissionApplicationOptions}
                  permissions={permissions.filter(
                    (p) => !values.permissionIds.some((pm) => +pm === p.permissionId)
                  )}
                  form={form}
                  name='permissionIds'
                  label='Velg tilganger'
                  triggerOnChangeAlways
                  isInitiallyOpen
                  onChange={(p) => {
                    let newPermissions = values.permissionIds;
                    newPermissions = newPermissions.concat(p);
                    newPermissions = Array.from(new Set(newPermissions).values()); // Make unique
                    setValues({ ...values, permissionIds: newPermissions });
                    setShowAddPermissions(false);
                  }}
                  onClose={() => setShowAddPermissions(false)}
                  okLabel='Velg'
                />
              </>
            )}
          </Col>
        </Row>
      </FormContainer>
    </Container>
  );
}

export default RoleForm;
